import {
  setNewEpisodeBackground,
  setSelectedTvShowSeasonEpisode,
  setSelectedTvShowSeasonEpisodeData,
  setSelectedTvShowSeasonResult,
  setTvShowSeasonEpisodesData,
} from '../slices/addTvShowSeasonEpisodeSlice';
import { setTvShowSeasonData } from '../slices/addTvShowSeasonSlice';
import { setTvShowData } from '../slices/addTvShowSlice';
import { RootState } from '../store';
import { buildEpisodeData } from './buildEpisodeData';
import { buildCatalogQuery, catalogCacheKey } from './catalogQuery';
import { mediaManagerServerApi } from './mediaManagerServerApi';
import { pollIngestionJobUntilDone } from './pollIngestionJob';

/**
 * What a subtitle check puts in its body.
 *
 * Named and exported so it can be tested on its own. It was written
 * inline as `{ name, content }`, and when the panel started sending the
 * row's language that field was simply dropped - a body built by naming
 * fields keeps only the fields it names. Everything either side looked
 * right, so nothing failed: the warning for a subtitle in the wrong row
 * was sent and never arrived.
 */
/** Everything the check is told about the file being checked. */
export type CheckSubtitleData = {
  name: string;
  content: string;
  /**
   * Optional to pass, never optional to name. A row may genuinely have
   * no language chosen yet, and the backend treats absent and undefined
   * alike - but the key is written out below either way, because the
   * whole failure being guarded against here is a field quietly not
   * being in the body.
   */
  language?: string | null;
};

/**
 * Written out field by field against the type rather than spread, so
 * that adding one to CheckSubtitleData and forgetting it here does not
 * compile. This is the body the row's language fell out of: it was
 * destructured from an untyped argument, the caller stopped passing
 * language, and the only sign was a warning that never appeared.
 */
export const checkSubtitleBody = (data: CheckSubtitleData) => {
  const body: {
    [K in keyof Required<CheckSubtitleData>]: CheckSubtitleData[K];
  } = {
    name: data.name,
    content: data.content,
    language: data.language,
  };
  return body;
};

export const extendedApiSlice = mediaManagerServerApi.injectEndpoints({
  endpoints: (builder) => ({
    grabEpisodeFrame: builder.mutation<any, any>({
      // The frame at one second of the episode's own file, to be its
      // cover. Queued, because only the machine holding the library
      // can open the file - the picture comes back in the finished
      // job rather than being stored anywhere, so a frame looked at
      // and turned down leaves nothing behind.
      query: ({
        id,
        seconds,
        crop = true,
      }: {
        id: number;
        seconds: number;
        crop?: boolean;
      }) => ({
        url: `tv_show/episodes/${id}/grab_frame/`,
        method: 'POST',
        body: { seconds, crop },
      }),
    }),

    getEpisodeVideoUrl: builder.query<any, any>({
      // Where the episode can be played from, signed. Needed before the
      // step that writes down where the file went, so the address is
      // worked out from where the file has to be.
      query: (id: number) => `tv_show/episodes/${id}/video_url/`,
    }),

    probeEpisodeAudio: builder.mutation<any, any>({
      // Only the machine holding the library can look at the file, so
      // this queues a job and comes back with its id to poll.
      query: (id: number) => ({
        url: `tv_show/episodes/${id}/probe_audio/`,
        method: 'POST',
      }),
    }),

    previewEpisodeSubtitle: builder.mutation<any, any>({
      // The film's address and the subtitle as WebVTT, to watch
      // the two together before either is committed to. Answers
      // at once rather than queueing: nothing here reads the
      // film, it only says where it is.
      //
      // The file goes up as bytes like the check's does, and
      // comes back converted, because a browser left to decide
      // what a subtitle is encoded in is how accents turn into
      // symbols - and a preview showing those would have somebody
      // fixing a file that was never broken.
      query: ({ id, content }) => ({
        url: `tv_show/episodes/${id}/preview_subtitle/`,
        method: 'POST',
        body: { content },
      }),
    }),

    checkEpisodeSubtitle: builder.mutation<any, any>({
      // Whether a subtitle a person has just picked belongs to this
      // episode. Only the machine holding the library can answer - it is
      // read off the episode's own sound - so this queues a job to poll.
      //
      // The file goes up with the request and is not stored. At this
      // point in the wizard nothing has been committed to, and one that
      // turns out to be another episode's should leave nothing behind.
      //
      // content is base64 of the file's bytes, not its text: a
      // downloaded subtitle can be in any encoding and the file does not
      // say which, so letting the browser decode it is how accents
      // become symbols. See components/CheckSubtitle/bytes.
      query: ({ id, ...sent }) => ({
        url: `tv_show/episodes/${id}/check_subtitle/`,
        method: 'POST',
        body: checkSubtitleBody(sent),
      }),
    }),

    fixEpisodeSubtitle: builder.mutation<any, any>({
      // Carrying out what the check proposed. Answers at once rather
      // than queueing: a shift, a stretch and a tidy are done to the
      // text and need neither the episode nor its sound.
      //
      // Not a detail route - there is no episode involved, only the
      // file. What comes back is the corrected file for the wizard to
      // hold until it is submitted, so it is saved and converted to
      // WebVTT once, like any other.
      query: (body) => ({
        url: 'tv_show/episodes/fix_subtitle/',
        method: 'POST',
        body,
      }),
    }),

    muxEpisode: builder.mutation<any, any>({
      // audioLanguages names each sound track's language in order, and
      // is only needed when the file has more than one: which track is
      // which cannot be worked out, only chosen.
      // dependsOn names a job that has to succeed first - the wizard
      // passes the finalize it has just queued, so the two go in
      // together and the file is written as soon as it has been read.
      query: ({
        id,
        audioLanguages,
        mediaLanguage,
        dependsOn,
        removeAudio,
        mainAudio,
      }: any) => ({
        url: `tv_show/episodes/${id}/mux/`,
        method: 'POST',
        body: {
          audio_languages: audioLanguages,
          media_language: mediaLanguage,
          depends_on: dependsOn,
          remove_audio: removeAudio,
          main_audio: mainAudio,
        },
      }),
      invalidatesTags: ['FinishingEpisode'],
    }),

    getTVShowCatalogFilterOptions: builder.query<any, void>({
      query: () => `tv_show/tv-shows/catalog_filter_options/`,
      transformResponse: (raw: any) => {
        const toOptions = (items: any[] = []) =>
          items.map((item: any) => ({
            value: item.label,
            label: item.label,
          }));
        return {
          genres: toOptions(raw?.genres),
          languages: toOptions(raw?.languages),
          countries: toOptions(raw?.countries),
          networks: toOptions(raw?.networks),
          tags: toOptions(raw?.tags),
          collections: toOptions(raw?.collections),
        };
      },
    }),

    getTVShows: builder.query<any, any | void>({
      query: (object) =>
        // See buildCatalogQuery: every value is encoded, and a filter
        // holding several values becomes a repeated parameter.
        `tv_show/tv-shows/?${buildCatalogQuery(object)}`,

      transformResponse: (rawResult: {
        results: any;
        currentPage: number;
        totalPages: number;
      }) => {
        // return rawResult.results;
        return {
          results: rawResult.results,
          currentPage: rawResult.currentPage,
          totalPages: rawResult.totalPages,
        };
      },
      // providesTags: ['TVShow'],
      // providesTags: (result = [], error, arg) => [
      // providesTags: (result = {}) => [
      //   'TVShow',
      //   ...result.results.map(({ id }: { id: number }) => ({
      //     type: 'TVShow',
      //     id,
      //   })),
      // ],

      // providesTags: (result, error, arg) =>
      providesTags: (result) =>
        result
          ? [
              ...result.results.map(({ id }: { id: number }) => ({
                type: 'TVShow' as const,
                id,
              })),
              'TVShow',
            ]
          : ['TVShow'],

      // Keyed on everything but the page, so all pages of one filter
      // set share an entry and merge below can append them. See
      // catalogQueryCacheKey for why the values are sorted and the empty
      // ones dropped - without that, the same filters chosen in a
      // different order would be a different entry and get refetched.
      // The key carries the endpoint name because the cache is one flat
      // map; without it the three catalogs share entries.
      serializeQueryArgs: catalogCacheKey,
      // Always merge incoming data to the cache entry
      merge: (currentCache, newItems) => {
        //#1
        currentCache.results.push(...newItems.results);
        currentCache.currentPage = newItems.currentPage;
        //#2
        // if (newItems.currentPage > currentCache.currentPage) {
        //   currentCache.results.push(...newItems.results);
        //   currentCache.currentPage = newItems.currentPage;
        // } else {
        //   currentCache.results = newItems.results;
        //   currentCache.currentPage = newItems.currentPage;
        // }
      },
      // Refetch when the page arg changes
      forceRefetch({ currentArg, previousArg }) {
        //#1
        return currentArg?.page > previousArg?.page;
        //#2
        // return currentArg?.page !== previousArg?.page;
      },
    }),
    getTVShow: builder.query<any, string | undefined>({
      query: (id) => `tv_show/tv-shows/${id}/`,
      // providesTags: ['TVShow'],
      providesTags: (result, error, arg) => [{ type: 'TVShow', id: arg }],
    }),

    getSimilarTVShows: builder.query<any, any | void>({
      query: (object) =>
        `tv_show/tv-shows/get_similar_tv_shows/?mode=${object?.mode || ''}&id=${object?.id || ''}`,
      // providesTags: ['Movie'],
      providesTags: (result, error, arg) => [{ type: 'TVShow', id: arg }],
    }),

    getTVShowSeason: builder.query<any, string | undefined>({
      query: (id) => `tv_show/seasons/${id}/`,
      // providesTags: ['TVShowSeason'],
      providesTags: (result, error, arg) => [
        { type: 'TVShowSeason', id: arg },
      ],
    }),

    getTVShowSeasonEpisode: builder.query<any, string | undefined>({
      query: (id) => `tv_show/episodes/${id}/`,
      // providesTags: ['TVShowSeasonEpisode'],
      providesTags: (result, error, arg) => [
        { type: 'TVShowSeasonEpisode', id: arg },
      ],
    }),

    getItunesAndImdbTvOptions: builder.query<any, any | void>({
      query: (object) =>
        `tv_show/tv-shows/get_itunes_and_imdb_options/?tv_show_title=${encodeURIComponent(object.tvShowTitle)}&itunes_country=${object?.itunesCountry ? object.itunesCountry.value : null}`,
      transformResponse: (rawResult: {
        itunesSearchResults: any;
        imdbSearchResults: any;
      }) => {
        const processedItunesSearchResults = rawResult.itunesSearchResults.map(
          (searchResult: any) => {
            return {
              title: searchResult.collectionName,
              cover: searchResult.artworkUrl100.replace(
                '100x100bb',
                '100000x100000-999',
              ),
              id: searchResult.collectionId,
            };
          },
        );
        // console.log(
        //   'processedItunesSearchResults:',
        //   processedItunesSearchResults,
        // );
        const processedImdbSearchResults = rawResult.imdbSearchResults.map(
          (searchResult: any) => {
            return {
              title: searchResult.Title,
              cover: searchResult.Poster,
              id: searchResult.imdbID,
            };
          },
        );
        return {
          itunesSearchMovieResults: processedItunesSearchResults,
          imdbSearchMovieResults: processedImdbSearchResults,
        };
      },
    }),

    getTvMediaData: builder.query<any, any | void>({
      queryFn: async (object, api, _extraOptions, baseQuery) => {
        // const state = api.getState() as RootState;
        try {
          const result: any = await baseQuery({
            url: `tv_show/tv-shows/get_media_data/?itunes_tv_show_id=${object.selectedItunesResult}&imdb_tv_show_id=${object.selectedImdbResult}&itunes_country=${object.selectedItunesCountry}`,
          });
          if (result?.error) {
            throw result.error;
          }
          const initialData = result.data;

          // Backgrounded server-side (see core/ingestion_jobs.py) -
          // this scrape can run several minutes for a large cast,
          // which used to exceed Azure App Service's hard ~240s
          // platform timeout and silently kill the request.
          // Encapsulated entirely here so callers see no difference.
          const finishedJob = await pollIngestionJobUntilDone(
            api.dispatch,
            extendedApiSlice.endpoints.getIngestionJob,
            initialData.job.id,
          );
          const scrapedTvShowData = finishedJob.resultData;

          const generalTvShowData = {
            title: scrapedTvShowData.title,
            originalTitle: scrapedTvShowData.originalTitle,
            shortDescription: scrapedTvShowData.shortDescription,
            description: scrapedTvShowData.description,
            startYear: scrapedTvShowData.startYear,
            endYear: scrapedTvShowData.endYear,
            imdbId: scrapedTvShowData.imdbId,
            imdbRating: scrapedTvShowData.imdbRating,
            rating: scrapedTvShowData.rating,
            // network: scrapedTvShowData.network,
          };

          const genreData = scrapedTvShowData.genres.map((genreItem: any) => {
            return {
              genre: {
                id: genreItem.id,
                label: genreItem.label,
              },
              main: genreItem.main,
            };
          });

          const countryData = scrapedTvShowData.countries.map(
            (countryItem: any) => {
              return {
                country: {
                  id: countryItem.id,
                  label: countryItem.label,
                },
                main: countryItem.main,
              };
            },
          );

          const secondaryTvShowData = {
            genres: genreData,
            countries: countryData,
            tags: scrapedTvShowData.tags,
            collections: scrapedTvShowData.collections,
          };

          // const castCrewTvShowSeasonEpisodeData = {
          //   cast: data.tvShowSeasonEpisodeData.cast,
          //   directors: data.tvShowSeasonEpisodeData.directors,
          //   producers: data.tvShowSeasonEpisodeData.producers,
          //   screenwriters: data.tvShowSeasonEpisodeData.screenwriters,
          // };

          // const languagesTvShowSeasonEpisodeData = data.tvShowSeasonEpisodeData.languages.map((languageItem: any) => {
          //   return {
          //     language: {
          //       id: languageItem.id,
          //       label: languageItem.label,
          //     },
          //     main: languageItem.main,
          //   };
          // });

          // const languageData = data.tvShowSeasonEpisodeData.languages.map(
          //   (languageItem: any) => {
          //     return {
          //       language: {
          //         id: languageItem.id,
          //         label: languageItem.label,
          //       },
          //       main: languageItem.main,
          //       audioTrack: false,
          //       subtitle: false,
          //     };
          //   },
          // );

          // const languagesTvShowSeasonEpisodeData = {
          //   languages: languageData,
          // };

          const tvShowData = {
            generalTvShowData: generalTvShowData,
            secondaryTvShowData: secondaryTvShowData,
            // castCrewMovieData: castCrewMovieData,
            // languagesMovieData: languagesMovieData,
            // studios: initialData.studios,
            ratings: initialData.ratings,
            // qualities: initialData.qualities,
            // formats: initialData.formats,
            // sources: initialData.sources,
            // networks: initialData.networks,

            genres: initialData.genres,
            countries: initialData.countries,
            tags: initialData.tags,
            collections: initialData.collections,

            // languages: initialData.languages,

            accessLevels: initialData.accessLevels,
          };

          api.dispatch(setTvShowData(tvShowData));

          // "genres": new_genres,
          // "languages": new_languages,
          // "countries": new_countries,
          // "tags": [{}],
          // "collections": [{}],
          // # "runtime": 0,
          // "cast": cast,
          // "directors": directors,
          // "screenwriters": screenwriters,
          // "producers": producers,
          // "editing_completed": False,

          // const usageParameters = state.config.usageParameters;
          // const { currency, amount, concept, externalId, bankCodes } = data;
          // const newUsageParameters = {
          //   ...usageParameters,
          //   currency,
          //   amount,
          //   concept,
          //   externalId,
          //   bankCodes,
          // } as IUsageParameters;
          // api.dispatch(setUsageParameters(newUsageParameters));
          // const countries = state.config.countries;
          // if (countries.length === 1) {
          //   const filteredBanks = filterBanks(countries[0], bankCodes);
          //   if (!filteredBanks) {
          //     throw { status: 'invalidBanks', error: 'Invalid banks' };
          //   } else {
          //     const newCountryData = { ...countries[0] };
          //     newCountryData.banks = filteredBanks;
          //     api.dispatch(setCountries([newCountryData]));
          //   }
          // }
          return { data: { ...initialData, tvShowData: scrapedTvShowData } };
        } catch (errorData: any) {
          // `onError` side-effect
          // console.log('ERROR ENTERED');
          return { error: errorData };
        }
      },
    }),

    createTvShow: builder.mutation({
      query: (data) => ({
        url: `tv_show/tv-shows/`,
        method: 'POST',
        body: data,
        // headers: {
        //   'Content-Type': 'multipart/form-data;'
        // },
        // formData: true,
      }),
    }),

    patchTvShow: builder.mutation<any | any, any>({
      queryFn: async (data, api, _extraOptions, baseQuery) => {
        const state = api.getState() as RootState;
        const newTvShowId = state.addTvShow.newTvShowId;
        const currentStep = state.addTvShow.currentStep;
        let formData = new FormData();
        // console.log('PRE DATA:', data);
        formData.append('step', data.step);
        if (currentStep === 5) {
          formData.append('cover', data.cover);
          formData.append('background', data.background);
        } else if (currentStep === 6) {
          formData = data;
        }

        try {
          const result: any = await baseQuery({
            url: `tv_show/tv-shows/${newTvShowId}/`,
            method: 'PATCH',
            body: formData,
          });
          if (result?.error) {
            throw result.error;
          }
          const data = result.data;
          return { data: data };
        } catch (errorData: any) {
          // `onError` side-effect
          return { error: errorData };
        }
      },
    }),

    getTvShowOptions: builder.query<any, any | void>({
      query: (object) =>
        `tv_show/tv-shows/?keyword=${encodeURIComponent(object.tvShowTitle)}&mode=add_tv_show_season`,
      transformResponse: (rawResult: { results: any }) => {
        const processedTvShowSearchResults = rawResult.results.map(
          (searchResult: any) => {
            return {
              title: searchResult.title,
              cover: searchResult.cover,
              id: searchResult.id,
              imdbId: searchResult.imdbId,
            };
          },
        );
        // console.log(
        //   'processedTvShowSearchResults:',
        //   processedTvShowSearchResults,
        // );
        return {
          tvShowSearchResults: processedTvShowSearchResults,
        };
      },
    }),

    getItunesTvShowSeasonOptions: builder.query<any, any | void>({
      query: (object) =>
        `tv_show/seasons/get_itunes_options/?tv_show_title=${encodeURIComponent(object.tvShowTitle)}&itunes_country=${object?.itunesCountry ? object.itunesCountry.value : null}`,
      transformResponse: (rawResult: { itunesSearchResults: any }) => {
        const processedItunesSearchResults = rawResult.itunesSearchResults.map(
          (searchResult: any) => {
            return {
              title: searchResult.collectionName,
              cover: searchResult.artworkUrl100.replace(
                '100x100bb',
                '100000x100000-999',
              ),
              id: searchResult.collectionId,
            };
          },
        );
        // console.log(
        //   'processedItunesSearchResults:',
        //   processedItunesSearchResults,
        // );
        return {
          itunesSearchTvShowSeasonResults: processedItunesSearchResults,
        };
      },
    }),

    getTvSeasonMediaData: builder.query<any, any | void>({
      queryFn: async (object, api, _extraOptions, baseQuery) => {
        // const state = api.getState() as RootState;
        try {
          const result: any = await baseQuery({
            url: `tv_show/seasons/get_media_data/?itunes_id=${object.selectedItunesResult}&imdb_id=${object.selectedImdbResult}&itunes_country=${object.selectedItunesCountry}&season=${object.season}`,
          });
          if (result?.error) {
            throw result.error;
          }
          const initialData = result.data;

          // Backgrounded server-side (see core/ingestion_jobs.py) -
          // see getMediaData/getTvMediaData for the full reasoning
          // (Azure App Service's hard ~240s platform timeout).
          const finishedJob = await pollIngestionJobUntilDone(
            api.dispatch,
            extendedApiSlice.endpoints.getIngestionJob,
            initialData.job.id,
          );
          const scrapedTvShowSeasonData = finishedJob.resultData;

          const generalTvShowSeasonData = {
            title: scrapedTvShowSeasonData.title,
            seasonNumber: scrapedTvShowSeasonData.seasonNumber,
            shortDescription: scrapedTvShowSeasonData.shortDescription,
            description: scrapedTvShowSeasonData.description,
            copyright: scrapedTvShowSeasonData.copyright,
            network: scrapedTvShowSeasonData.network,
            itunesId: scrapedTvShowSeasonData.itunesId,
            trailerUrl: scrapedTvShowSeasonData.trailerUrl,
          };

          const castCrewTvShowSeasonData = {
            cast: scrapedTvShowSeasonData.cast,
            producers: scrapedTvShowSeasonData.producers,
          };

          const tvShowSeasonData = {
            generalTvShowSeasonData: generalTvShowSeasonData,
            castCrewTvShowSeasonData: castCrewTvShowSeasonData,
            networks: initialData.networks,
          };

          api.dispatch(setTvShowSeasonData(tvShowSeasonData));

          return {
            data: {
              ...initialData,
              tvShowSeasonData: scrapedTvShowSeasonData,
            },
          };
        } catch (errorData: any) {
          // `onError` side-effect
          // console.log('ERROR ENTERED');
          return { error: errorData };
        }
      },
    }),

    createTvShowSeason: builder.mutation({
      query: (data) => ({
        url: `tv_show/seasons/`,
        method: 'POST',
        body: data,
        // headers: {
        //   'Content-Type': 'multipart/form-data;'
        // },
        // formData: true,
      }),
    }),

    patchTvShowSeason: builder.mutation<any | any, any>({
      queryFn: async (data, api, _extraOptions, baseQuery) => {
        const state = api.getState() as RootState;
        const newTvShowSeasonId = state.addTvShowSeason.newTvShowSeasonId;
        const currentStep = state.addTvShowSeason.currentTvShowSeasonStep;
        const formData = new FormData();
        // console.log('PRE DATA:', data);
        formData.append('step', data.step);
        if (currentStep === 6) {
          formData.append('cover', data.cover);
          formData.append('background', data.background);
        }

        try {
          const result: any = await baseQuery({
            url: `tv_show/seasons/${newTvShowSeasonId}/`,
            method: 'PATCH',
            body: formData,
          });
          if (result?.error) {
            throw result.error;
          }
          const data = result.data;
          return { data: data };
        } catch (errorData: any) {
          // `onError` side-effect
          return { error: errorData };
        }
      },
    }),

    getTvShowSeasonOptions: builder.query<any, any | void>({
      query: (object) =>
        `tv_show/seasons/?keyword=${object.tvShowTitle}&mode=add_tv_show_season_episode`,
      transformResponse: (rawResult: { results: any }) => {
        // const processedTvShowSeasonSearchResults = rawResult.results.map(
        //   (searchResult: any) => {
        //     return {
        //       title: `${searchResult.title}, Season ${searchResult.number}`,
        //       cover: searchResult.cover,
        //       id: searchResult.id,
        //     };
        //   },
        // );
        const processedTvShowSeasonSearchResults = rawResult.results;
        return {
          tvShowSeasonSearchResults: processedTvShowSeasonSearchResults,
        };
      },
    }),

    getTvSeasonEpisodes: builder.query<any, any | void>({
      queryFn: async (object, api, _extraOptions, baseQuery) => {
        // const state = api.getState() as RootState;
        try {
          const result: any = await baseQuery({
            url: `tv_show/episodes/get_episodes/?season_id=${object.selectedSeason}`,
          });
          if (result?.error) {
            throw result.error;
          }
          const data = result.data;

          // const generalTvShowSeasonData = {
          //   title: data.tvShowSeasonData.title,
          //   seasonNumber: data.tvShowSeasonData.seasonNumber,
          //   shortDescription: data.tvShowSeasonData.shortDescription,
          //   description: data.tvShowSeasonData.description,
          //   copyright: data.tvShowSeasonData.copyright,
          //   network: data.tvShowSeasonData.network,
          //   itunesId: data.tvShowSeasonData.itunesId,
          //   trailerUrl: data.tvShowSeasonData.trailerUrl,
          // };

          // const castCrewTvShowSeasonData = {
          //   cast: data.tvShowSeasonData.cast,
          //   producers: data.tvShowSeasonData.producers,
          // };

          // const tvShowSeasonData = {
          //   generalTvShowSeasonData: generalTvShowSeasonData,
          //   castCrewTvShowSeasonData: castCrewTvShowSeasonData,
          //   networks: data.networks,
          // };

          api.dispatch(
            setTvShowSeasonEpisodesData(data.tvShowSeasonEpisodesData),
          );

          return { data: data };
        } catch (errorData: any) {
          // `onError` side-effect
          // console.log('ERROR ENTERED');
          return { error: errorData };
        }
      },
    }),

    getRandomEpisode: builder.query<any, any | void>({
      query: (object) =>
        `tv_show/episodes/get_random_episode/?mode=${object?.mode || ''}`,
      providesTags: (result, error, arg) => [
        { type: 'TVShowSeasonEpisode', id: arg },
      ],
    }),

    getTvSeasonEpisodeMediaData: builder.query<any, any | void>({
      queryFn: async (object, api, _extraOptions, baseQuery) => {
        // const state = api.getState() as RootState;
        try {
          // The number and title the episode list already showed,
          // sent along because OMDb has no record of some episodes
          // and then nothing else knows them. See
          // Process.episode_from_imdb_alone.
          const alreadyKnown = object.picked
            ? `&episode_number=${encodeURIComponent(
                object.picked.number ?? '',
              )}&episode_title=${encodeURIComponent(
                object.picked.title ?? '',
              )}`
            : '';
          const result: any = await baseQuery({
            url: `tv_show/episodes/get_media_data/?season_id=${object.selectedSeason}&episode_imdb_id=${object.selectedEpisode}${alreadyKnown}`,
          });
          if (result?.error) {
            throw result.error;
          }
          const initialData = result.data;

          // Backgrounded server-side (see core/ingestion_jobs.py) -
          // see getMediaData/getTvMediaData for the full reasoning
          // (Azure App Service's hard ~240s platform timeout). Lower
          // risk here (a single episode, no cast/crew loop) but the
          // same class of risk, so covered by the same pattern.
          const finishedJob = await pollIngestionJobUntilDone(
            api.dispatch,
            extendedApiSlice.endpoints.getIngestionJob,
            initialData.job.id,
          );
          const scrapedEpisodeData = finishedJob.resultData;

          // Shared with the queued flow (see continueQueuedEpisode),
          // which produces the identical payload and has to land in
          // redux identically - otherwise every step after this one
          // would need to know which flow it came from.
          api.dispatch(
            setSelectedTvShowSeasonEpisodeData(
              buildEpisodeData(scrapedEpisodeData, initialData),
            ),
          );
          return {
            data: {
              ...initialData,
              tvShowSeasonEpisodeData: scrapedEpisodeData,
            },
          };
        } catch (errorData: any) {
          // `onError` side-effect
          // console.log('ERROR ENTERED');
          return { error: errorData };
        }
      },
    }),

    createTvShowSeasonEpisode: builder.mutation({
      query: (data) => ({
        url: `tv_show/episodes/`,
        method: 'POST',
        body: data,
        // headers: {
        //   'Content-Type': 'multipart/form-data;'
        // },
        // formData: true,
      }),
    }),

    patchTvShowSeasonEpisode: builder.mutation<any | any, any>({
      queryFn: async (data, api, _extraOptions, baseQuery) => {
        const state = api.getState() as RootState;
        const newTvShowSeasonEpisodeId =
          state.addTvShowSeasonEpisode.newTvShowSeasonEpisodeId;
        const currentTvShowSeasonEpisodeStep =
          state.addTvShowSeasonEpisode.currentTvShowSeasonEpisodeStep;
        let formData = new FormData();
        // console.log('PRE DATA:', data);
        formData.append('step', data.step);
        if (currentTvShowSeasonEpisodeStep === 5) {
          formData.append('cover', data.cover);
          formData.append('background', data.background);
        } else if (currentTvShowSeasonEpisodeStep === 6) {
          formData = data;
        } else if (currentTvShowSeasonEpisodeStep === 7) {
          // formData.append('languages', data.languages)

          for (let i = 0; i < data.languages.length; i++) {
            formData.append('subtitlesSrt', data.languages[i]?.subtitleSrt);
          }

          // for (let i = 0; i < data.languages.length; i++) {
          //   formData.append('subtitlesVtt', data.languages[i]?.subtitleVtt);
          // }

          // formData.delete("languages")
          for (let i = 0; i < data.languages.length; i++) {
            data.languages[i].subtitleSrt =
              data.languages[i]?.subtitleSrt?.name;
            // data.languages[i].subtitleVtt =
            //   data.languages[i]?.subtitleVtt?.name;
          }
          formData.append('languages', JSON.stringify(data.languages));
        } else if (currentTvShowSeasonEpisodeStep === 8) {
          formData = data;
        }

        try {
          const result: any = await baseQuery({
            url: `tv_show/episodes/${newTvShowSeasonEpisodeId}/`,
            method: 'PATCH',
            body: formData,
          });
          if (result?.error) {
            throw result.error;
          }
          const data = result.data;
          return { data: data };
        } catch (errorData: any) {
          // `onError` side-effect
          return { error: errorData };
        }
      },
    }),

    getEpisodeUser: builder.query<any, string | undefined>({
      query: (id) => `tv_show/episode_users/${id}/`,
      // providesTags: ['Movie'],
      providesTags: (result, error, arg) => [{ type: 'EpisodeUser', id: arg }],
    }),

    saveEpisodeUser: builder.mutation({
      query: (data) => ({
        url: `tv_show/episode_users/`,
        method: 'POST',
        body: data,
        // headers: {
        //   'Content-Type': 'multipart/form-data;'
        // },
        // formData: true,
      }),
      // invalidatesTags: (result, error, arg) => [{ type: 'EpisodeUser' }],
      invalidatesTags: () => [{ type: 'EpisodeUser' }],
    }),

    getTVShowSeasonEpisodeBackground: builder.query<any, void>({
      queryFn: async (object, api, _extraOptions, baseQuery) => {
        const state = api.getState() as RootState;
        const newTvShowSeasonEpisodeId =
          state.addTvShowSeasonEpisode.newTvShowSeasonEpisodeId;
        // const newMovieId = 100;
        try {
          const result: any = await baseQuery({
            url: `tv_show/episodes/get_episode_background/?episode_id=${newTvShowSeasonEpisodeId}`,
          });
          if (result?.error) {
            throw result.error;
          }
          const data = result.data;
          api.dispatch(setNewEpisodeBackground(data));
          return { data: data };
        } catch (errorData: any) {
          // `onError` side-effect
          // console.log('ERROR ENTERED');
          return { error: errorData };
        }
      },
    }),

    getArtistTVShowData: builder.query<any, any | void>({
      queryFn: async (object, api, _extraOptions, baseQuery) => {
        try {
          const result: any = await baseQuery({
            url: `tv_show/episodes/get_artist_tv_show_data/?artist_id=${object.artistId}&tv_show_id=${object.tvShowId}`,
          });
          if (result?.error) {
            throw result.error;
          }
          const data = result.data.artistTvShowData;
          return { data: data };
        } catch (errorData: any) {
          // `onError` side-effect
          // console.log('ERROR ENTERED');
          return { error: errorData };
        }
      },
    }),

    // Episode creation is backgrounded server-side (it scrapes IMDB
    // for every cast/crew member's photo, which can take minutes for
    // a large cast) - createTvShowSeasonEpisode returns a job
    // immediately, and the caller polls this to find out when it's
    // actually done.
    getIngestionJob: builder.query<any, string>({
      query: (jobId) => `core/ingestion-jobs/${jobId}/`,
    }),
    retryIngestionJob: builder.mutation<any, string>({
      query: (jobId) => ({
        url: `core/ingestion-jobs/${jobId}/retry/`,
        method: 'POST',
      }),
    }),

    // A whole selection at once, because that is how the picker works
    // in queue mode - one round trip rather than one per episode.
    queueEpisodeMediaData: builder.mutation<any, any>({
      query: (object) => ({
        url: `tv_show/episodes/queue_media_data/`,
        method: 'POST',
        body: {
          seasonId: object.seasonId,
          episodes: object.episodes,
        },
      }),
      invalidatesTags: ['QueuedEpisode'],
    }),

    getQueuedEpisodes: builder.query<any, any | void>({
      query: (object) =>
        `core/ingestion-jobs/queued/?model_name=episode&page=${object?.page || 1}`,
      providesTags: ['QueuedEpisode'],
    }),

    // The episodes whose file is being written. The movie file has the
    // same thing for films; the per-service duplication is the
    // convention here rather than an oversight, and both point at the
    // same generic endpoint.
    getFinishingEpisodes: builder.query<any, any | void>({
      query: (object) =>
        `core/ingestion-jobs/finishing/?model_name=episode&page=${
          object?.page || 1
        }`,
      providesTags: ['FinishingEpisode'],
    }),

    // Picks a finished queued scrape back up. Does more than the movie
    // equivalent, because an episode is not self-contained: as well as
    // the scraped payload it has to put back the season it belongs to,
    // the episode that was chosen, and the season's episode list - the
    // last of which later steps read for the episode count.
    continueQueuedEpisode: builder.mutation<any, string>({
      queryFn: async (jobId, api, _extraOptions, baseQuery) => {
        try {
          const result: any = await baseQuery({
            url: `tv_show/episodes/queued_media_data/?job_id=${jobId}`,
          });
          if (result?.error) {
            throw result.error;
          }
          const data = result.data;

          api.dispatch(
            setSelectedTvShowSeasonEpisodeData(
              buildEpisodeData(data.episodeData, data),
            ),
          );
          api.dispatch(
            setSelectedTvShowSeasonResult({
              id: data.seasonId,
              title: data.seasonTitle,
            }),
          );
          api.dispatch(setSelectedTvShowSeasonEpisode(data.episodeImdbId));

          // The season's episode list is deliberately not fetched here.
          // get_episodes is a live Selenium scrape, and from Azure every
          // WebDriver command crosses the Cloudflare tunnel one at a
          // time - waiting on it left Continue sitting silently for
          // about a minute before the form appeared.
          //
          // Nothing reads that list until step 8, which is past this
          // form, the cast and crew, and a creation scrape of several
          // minutes, so step 8 fetches it itself if it is still
          // missing - later, and off the path of a button press.
          return { data };
        } catch (errorData: any) {
          return { error: errorData };
        }
      },
    }),
  }),
});

export const {
  useProbeEpisodeAudioMutation,
  usePreviewEpisodeSubtitleMutation,
  useCheckEpisodeSubtitleMutation,
  useFixEpisodeSubtitleMutation,
  useMuxEpisodeMutation,
  useGetTVShowsQuery,
  useGetTVShowCatalogFilterOptionsQuery,
  useGetTVShowQuery,
  useGetTVShowSeasonQuery,
  useGetTVShowSeasonEpisodeQuery,
  useGetItunesAndImdbTvOptionsQuery,
  useGetTvMediaDataQuery,
  useCreateTvShowMutation,
  usePatchTvShowMutation,
  useGetTvShowOptionsQuery,
  useGetItunesTvShowSeasonOptionsQuery,
  useGetTvSeasonMediaDataQuery,
  useCreateTvShowSeasonMutation,
  usePatchTvShowSeasonMutation,
  useGetTvShowSeasonOptionsQuery,
  useGetTvSeasonEpisodesQuery,
  useGetRandomEpisodeQuery,
  useGetTvSeasonEpisodeMediaDataQuery,
  useCreateTvShowSeasonEpisodeMutation,
  usePatchTvShowSeasonEpisodeMutation,
  useGetEpisodeUserQuery,
  useSaveEpisodeUserMutation,
  useGetSimilarTVShowsQuery,
  useGetTVShowSeasonEpisodeBackgroundQuery,
  useGetArtistTVShowDataQuery,
  useQueueEpisodeMediaDataMutation,
  useGetQueuedEpisodesQuery,
  useGetFinishingEpisodesQuery,
  useContinueQueuedEpisodeMutation,
  useLazyGetIngestionJobQuery,
  useRetryIngestionJobMutation,
  useGrabEpisodeFrameMutation,
  useLazyGetEpisodeVideoUrlQuery,
} = extendedApiSlice;
